import api from "../../../core/api/interceptors/axiosInterceptor";

/**
 * Get all sale orders for the current user/company
 */
export const getSaleOrders = async () => {
  try {
    const response = await api.get("SaleOrders");
    return response.data;
  } catch (error) {
    console.error("Error fetching sale orders:", error);
    throw error;
  }
};

/**
 * Get a single sale order by ID
 */
export const getSaleOrderById = async (id) => {
  try {
    const response = await api.get(`SaleOrders/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Get the next sale order number
 */
export const getNextSaleOrderNumber = async () => {
  try {
    const response = await api.get("SaleOrders/next-number");
    return response.data;
  } catch (error) {
    console.error("Error fetching next sale order number:", error);
    return `SO-${Date.now() % 10000}`;
  }
};

/**
 * Add a new sale order (draft or finalized)
 */
export const addSaleOrder = async (saleOrderData) => {
  try {
    const response = await api.post("SaleOrders", saleOrderData);
    return response.data;
  } catch (error) {
    console.error("Error adding sale order:", error);
    throw error;
  }
};

/**
 * Update an existing sale order
 */
export const updateSaleOrder = async (id, saleOrderData) => {
  try {
    const response = await api.put(`SaleOrders/${id}`, saleOrderData);
    return response.data;
  } catch (error) {
    console.error(`Error updating sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Add a product/line item to an existing sale order
 */
export const addSaleOrderLineItem = async (saleOrderId, lineItemData) => {
  try {
    const response = await api.post(`SaleOrders/${saleOrderId}/line-items`, lineItemData);
    return response.data;
  } catch (error) {
    console.error(`Error adding line item to sale order ${saleOrderId}:`, error);
    throw error;
  }
};

/**
 * Update a line item in a sale order
 */
export const updateSaleOrderLineItem = async (saleOrderId, lineItemId, lineItemData) => {
  try {
    const response = await api.put(`SaleOrders/${saleOrderId}/line-items/${lineItemId}`, lineItemData);
    return response.data;
  } catch (error) {
    console.error(`Error updating line item ${lineItemId}:`, error);
    throw error;
  }
};

/**
 * Delete a line item from a sale order
 */
export const deleteSaleOrderLineItem = async (saleOrderId, lineItemId) => {
  try {
    const response = await api.delete(`SaleOrders/${saleOrderId}/line-items/${lineItemId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting line item ${lineItemId}:`, error);
    throw error;
  }
};

/**
 * Delete a sale order
 */
export const deleteSaleOrder = async (id) => {
  try {
    const response = await api.delete(`SaleOrders/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Close a sale order (mark as closed/finalized)
 */
export const closeSaleOrder = async (id) => {
  try {
    const response = await api.post(`SaleOrders/${id}/close`);
    return response.data;
  } catch (error) {
    console.error(`Error closing sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Mark sale order as draft
 */
export const markSaleOrderAsDraft = async (id) => {
  try {
    const response = await api.post(`SaleOrders/${id}/mark-draft`);
    return response.data;
  } catch (error) {
    console.error(`Error marking sale order ${id} as draft:`, error);
    throw error;
  }
};

/**
 * Convert sale order to invoice
 */
export const convertSaleOrderToInvoice = async (id) => {
  try {
    const response = await api.post(`SaleOrders/${id}/convert-to-invoice`);
    return response.data;
  } catch (error) {
    console.error(`Error converting sale order ${id} to invoice:`, error);
    throw error;
  }
};

/**
 * Record a payment for a sale order
 */
export const recordSaleOrderPayment = async (id, paymentData) => {
  try {
    const response = await api.post(`SaleOrders/${id}/payments`, paymentData);
    return response.data;
  } catch (error) {
    console.error(`Error recording payment for sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Get payment history for a sale order
 */
export const getSaleOrderPayments = async (id) => {
  try {
    const response = await api.get(`SaleOrders/${id}/payments`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payments for sale order ${id}:`, error);
    throw error;
  }
};

/**
 * Send sale order via email
 */
export const sendSaleOrderEmail = async (id, emailData) => {
  try {
    const response = await api.post(`SaleOrders/${id}/send-email`, emailData);
    return response.data;
  } catch (error) {
    console.error(`Error sending sale order ${id} via email:`, error);
    throw error;
  }
};

/**
 * Search sale orders
 */
export const searchSaleOrders = async (searchTerm) => {
  try {
    const response = await api.get("SaleOrders/search", {
      params: { query: searchTerm },
    });
    return response.data;
  } catch (error) {
    console.error("Error searching sale orders:", error);
    throw error;
  }
};

/**
 * Get sale orders by status
 */
export const getSaleOrdersByStatus = async (status) => {
  try {
    const response = await api.get("SaleOrders/by-status", {
      params: { status },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching sale orders by status ${status}:`, error);
    throw error;
  }
};

/**
 * Get sale orders by payment status
 */
export const getSaleOrdersByPaymentStatus = async (paymentStatus) => {
  try {
    const response = await api.get("SaleOrders/by-payment-status", {
      params: { paymentStatus },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching sale orders by payment status ${paymentStatus}:`, error);
    throw error;
  }
};
